import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createCompany,
  deleteCompany,
  disableCompany,
  enableCompany,
  getCompanies,
  getCompanyBranches,
  getCompanyProvisioningJobs,
  restoreCompany,
  retryCompanyProvisioning,
  updateCompany,
} from '../api/companyApi';
import useConfirmDialog from '../hooks/useConfirmDialog';
import InlineLoading from './InlineLoading';
import { clearCurrentUserCache } from '../hooks/useCurrentUser';
import {
    clearSelectedCompanyContext,
    getSelectedCompanyId,
    persistSelectedCompanyId,
} from '../utils/selectedCompany';

export const COMPANY_STATUS_TABS = [
  { id: 'active', label: 'Activas' },
  { id: 'disabled', label: 'Deshabilitadas' },
  { id: 'provisioning', label: 'Aprovisionando' },
  { id: 'failed', label: 'Con error' },
  { id: 'deleted', label: 'Eliminadas' },
];

export const COMPANY_COPY_GROUPS = [
  { id: 'catalog', label: 'Catálogo', description: 'Productos, formatos, categorías, impuestos y medios.' },
  { id: 'prices', label: 'Precios', description: 'Disponibilidad y precios vigentes de una sucursal.' },
  { id: 'clients', label: 'Clientes', description: 'Perfiles, crédito, categorías y descuentos; sin credenciales.' },
  { id: 'providers', label: 'Proveedores', description: 'Perfiles y cuentas bancarias.' },
  { id: 'settings', label: 'Configuración', description: 'Opciones portables y referencias que puedan mapearse.' },
];

const emptyForm = () => ({
  name: '',
  firstBranchName: '',
  sourceCompanyId: '',
  sourceBranchId: '',
  copyGroups: [],
});

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.companies)) return payload.companies;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.branches)) return payload.branches;
  if (Array.isArray(payload?.jobs)) return payload.jobs;
  return [];
};

export const normalizeCompanyStatus = (company) => {
  if (company?.deletedAt || company?.isDeleted) return 'deleted';
  return String(company?.status || (company?.isEnabled === false ? 'disabled' : 'active')).toLowerCase();
};

export const normalizeCompanyCopyGroups = (groups) => {
  const selected = new Set(Array.isArray(groups) ? groups : []);
  if (selected.has('prices') || selected.has('clients')) selected.add('catalog');
  return COMPANY_COPY_GROUPS.map((entry) => entry.id).filter((id) => selected.has(id));
};

const statusStyles = {
  active: 'bg-green-100 text-green-700',
  disabled: 'bg-gray-100 text-gray-700',
  provisioning: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  deleted: 'bg-amber-100 text-amber-800',
};

const statusLabels = {
  active: 'Activa',
  disabled: 'Deshabilitada',
  provisioning: 'Aprovisionando',
  failed: 'Con error',
  deleted: 'Eliminada',
};

const getProvisioning = (company) => company?.provisioningJob
  || company?.CompanyProvisioningJob
  || company?.provisioning
  || {};

const CompaniesSettingsPanel = () => {
  const { confirm } = useConfirmDialog();
  const [companies, setCompanies] = useState([]);
  const [activeStatus, setActiveStatus] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionId, setActionId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [sourceBranches, setSourceBranches] = useState([]);
  const [sourceBranchesLoading, setSourceBranchesLoading] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editingName, setEditingName] = useState('');
  const hadProvisioningCompanies = useRef(false);

  const refreshSessionCompanies = useCallback(() => {
    const selectedCompanyId = getSelectedCompanyId();
    clearCurrentUserCache();
    persistSelectedCompanyId(selectedCompanyId, { clearBranch: false });
  }, []);

  const loadCompanies = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const payload = await getCompanies({ includeDeleted: true });
      const listedCompanies = unwrapList(payload);
      const enrichedCompanies = await Promise.all(listedCompanies.map(async (company) => {
        const status = normalizeCompanyStatus(company);
        if (!['provisioning', 'failed'].includes(status)) return company;
        try {
          const jobsPayload = await getCompanyProvisioningJobs(company.id);
          const jobs = unwrapList(jobsPayload);
          return { ...company, provisioningJob: jobs[0] || null };
        } catch {
          return company;
        }
      }));
      setCompanies(enrichedCompanies);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'No se pudieron cargar las empresas.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const hasProvisioningCompanies = companies.some(
    (company) => normalizeCompanyStatus(company) === 'provisioning'
  );

  useEffect(() => {
    if (hasProvisioningCompanies) {
      hadProvisioningCompanies.current = true;
      return;
    }
    if (hadProvisioningCompanies.current) {
      hadProvisioningCompanies.current = false;
      refreshSessionCompanies();
    }
  }, [hasProvisioningCompanies, refreshSessionCompanies]);

  useEffect(() => {
    if (!hasProvisioningCompanies) return undefined;
    const timer = window.setInterval(() => loadCompanies({ silent: true }), 5000);
    return () => window.clearInterval(timer);
  }, [hasProvisioningCompanies, loadCompanies]);

  useEffect(() => {
    if (!form.sourceCompanyId) {
      setSourceBranches([]);
      setForm((current) => ({ ...current, sourceBranchId: '', copyGroups: [] }));
      return undefined;
    }
    let mounted = true;
    setSourceBranchesLoading(true);
    getCompanyBranches(form.sourceCompanyId)
      .then((payload) => {
        if (!mounted) return;
        setSourceBranches(unwrapList(payload).filter((branch) => !branch.isDeleted));
      })
      .catch((err) => {
        if (!mounted) return;
        setSourceBranches([]);
        setCreateError(err?.response?.data?.message || 'No se pudieron cargar las sucursales de origen.');
      })
      .finally(() => {
        if (mounted) setSourceBranchesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.sourceCompanyId]);

  const counts = useMemo(() => COMPANY_STATUS_TABS.reduce((result, tab) => ({
    ...result,
    [tab.id]: companies.filter((company) => normalizeCompanyStatus(company) === tab.id).length,
  }), {}), [companies]);

  const visibleCompanies = useMemo(() => companies.filter(
    (company) => normalizeCompanyStatus(company) === activeStatus
  ), [activeStatus, companies]);

  const sourceCompanies = useMemo(() => companies.filter(
    (company) => ['active', 'disabled'].includes(normalizeCompanyStatus(company))
  ), [companies]);

  const runAction = async (company, action, successMessage) => {
    setActionId(String(company.id));
    setError('');
    setNotice('');
    try {
      await action(company.id);
      if (String(getSelectedCompanyId()) === String(company.id)
        && ['disabled', 'deleted'].includes(successMessage.context)) {
        clearSelectedCompanyContext();
      }
      setNotice(successMessage.text);
      await loadCompanies({ silent: true });
      refreshSessionCompanies();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'No se pudo completar la acción.');
    } finally {
      setActionId('');
    }
  };

  const handleDisable = async (company) => {
    const approved = await confirm({
      title: 'Deshabilitar empresa',
      message: `Los usuarios dejarán de poder entrar a ${company.name}.`,
      confirmLabel: 'Deshabilitar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    runAction(company, disableCompany, { text: 'Empresa deshabilitada.', context: 'disabled' });
  };

  const handleDelete = async (company) => {
    if (company.isClientPortalDefault || company.isDefaultClientPortalCompany) return;
    const approved = await confirm({
      title: 'Eliminar empresa',
      message: `La empresa ${company.name} quedará eliminada de forma recuperable.`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    runAction(company, deleteCompany, { text: 'Empresa eliminada; puedes restaurarla más adelante.', context: 'deleted' });
  };

  const toggleCopyGroup = (groupId) => {
    setForm((current) => {
      const next = new Set(current.copyGroups);
      if (next.has(groupId)) {
        next.delete(groupId);
        if (groupId === 'catalog') {
          next.delete('prices');
          next.delete('clients');
        }
      } else {
        next.add(groupId);
      }
      return { ...current, copyGroups: normalizeCompanyCopyGroups(Array.from(next)) };
    });
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    const firstBranchName = form.firstBranchName.trim();
    if (!name || !firstBranchName) {
      setCreateError('El nombre de la empresa y su primera sucursal son obligatorios.');
      return;
    }
    if (form.copyGroups.includes('prices') && !form.sourceBranchId) {
      setCreateError('Selecciona una sucursal de origen para copiar precios.');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      await createCompany({
        name,
        firstBranchName,
        sourceCompanyId: form.sourceCompanyId || null,
        sourceBranchId: form.sourceBranchId || null,
        copyGroups: normalizeCompanyCopyGroups(form.copyGroups),
      });
      setForm(emptyForm());
      setShowCreate(false);
      setActiveStatus('provisioning');
      setNotice('Empresa creada. Estará disponible cuando termine el aprovisionamiento.');
      await loadCompanies({ silent: true });
      refreshSessionCompanies();
    } catch (err) {
      setCreateError(err?.response?.data?.message || err?.message || 'No se pudo crear la empresa.');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveName = async (company) => {
    const name = editingName.trim();
    if (!name) return;
    setActionId(String(company.id));
    setError('');
    try {
      await updateCompany(company.id, { name });
      setEditingId('');
      setEditingName('');
      setNotice('Nombre actualizado.');
      await loadCompanies({ silent: true });
      refreshSessionCompanies();
    } catch (err) {
      setError(err?.response?.data?.message || 'No se pudo actualizar la empresa.');
    } finally {
      setActionId('');
    }
  };

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Empresas</h2>
            <p className="mt-1 text-sm text-gray-500">
              Crea entornos aislados y administra su ciclo de vida. Solo las empresas activas aparecen en el selector operativo.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowCreate((current) => !current);
              setCreateError('');
            }}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            {showCreate ? 'Cerrar' : 'Nueva empresa'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} className="mt-5 space-y-5 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="text-xs font-semibold uppercase text-gray-600">
                Nombre de la empresa
                <input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  className="mt-1 h-10 w-full rounded border border-gray-300 bg-white px-3 text-sm font-normal normal-case"
                  placeholder="Nueva empresa"
                  disabled={creating}
                />
              </label>
              <label className="text-xs font-semibold uppercase text-gray-600">
                Primera sucursal
                <input
                  value={form.firstBranchName}
                  onChange={(event) => setForm((current) => ({ ...current, firstBranchName: event.target.value }))}
                  className="mt-1 h-10 w-full rounded border border-gray-300 bg-white px-3 text-sm font-normal normal-case"
                  placeholder="Sucursal principal"
                  disabled={creating}
                />
              </label>
              <label className="text-xs font-semibold uppercase text-gray-600">
                Empresa de origen (opcional)
                <select
                  value={form.sourceCompanyId}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    sourceCompanyId: event.target.value,
                    sourceBranchId: '',
                    copyGroups: [],
                  }))}
                  className="mt-1 h-10 w-full rounded border border-gray-300 bg-white px-3 text-sm font-normal normal-case"
                  disabled={creating}
                >
                  <option value="">Crear con valores iniciales</option>
                  {sourceCompanies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold uppercase text-gray-600">
                Sucursal de origen (opcional)
                <select
                  value={form.sourceBranchId}
                  onChange={(event) => setForm((current) => ({ ...current, sourceBranchId: event.target.value }))}
                  className="mt-1 h-10 w-full rounded border border-gray-300 bg-white px-3 text-sm font-normal normal-case"
                  disabled={creating || !form.sourceCompanyId || sourceBranchesLoading}
                >
                  <option value="">Sin sucursal de referencia</option>
                  {sourceBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.branchName || branch.name}</option>
                  ))}
                </select>
                {sourceBranchesLoading && <span className="mt-1 block text-xs normal-case text-gray-500">Cargando sucursales...</span>}
              </label>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-gray-600">Copiar desde el origen</p>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                {COMPANY_COPY_GROUPS.map((group) => (
                  <label key={group.id} className={`rounded-lg border p-3 ${form.sourceCompanyId ? 'bg-white' : 'bg-gray-50 opacity-60'}`}>
                    <span className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={form.copyGroups.includes(group.id)}
                        onChange={() => toggleCopyGroup(group.id)}
                        disabled={creating || !form.sourceCompanyId}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-gray-800">{group.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">{group.description}</span>
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Precios y Clientes incluyen Catálogo automáticamente. Nunca se copian usuarios, saldos, cajas, credenciales ni historial.
              </p>
            </div>

            {createError && <p role="alert" className="text-sm text-red-600">{createError}</p>}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={creating}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {creating ? 'Creando...' : 'Crear y aprovisionar'}
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto border-b border-gray-200 px-3 pt-3">
          <div className="flex min-w-max gap-1" role="tablist" aria-label="Estado de empresas">
            {COMPANY_STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeStatus === tab.id}
                onClick={() => setActiveStatus(tab.id)}
                className={`rounded-t-lg px-3 py-2 text-sm font-semibold ${
                  activeStatus === tab.id
                    ? 'border border-b-white border-gray-200 bg-white text-blue-700'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                {tab.label} <span className="ml-1 text-xs">({counts[tab.id] || 0})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {loading && <InlineLoading label="Cargando empresas" />}
          {error && <p role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {notice && <p className="mb-3 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">{notice}</p>}
          {!loading && visibleCompanies.length === 0 && (
            <p className="rounded border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
              No hay empresas en este estado.
            </p>
          )}

          <div className="space-y-3">
            {visibleCompanies.map((company) => {
              const status = normalizeCompanyStatus(company);
              const provisioning = getProvisioning(company);
              const progress = Number(provisioning.progress ?? company.provisioningProgress ?? 0);
              const isBusy = String(actionId) === String(company.id);
              const isPortalDefault = Boolean(company.isClientPortalDefault || company.isDefaultClientPortalCompany);
              const isEditing = String(editingId) === String(company.id);
              return (
                <article key={company.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {isEditing ? (
                          <input
                            value={editingName}
                            onChange={(event) => setEditingName(event.target.value)}
                            className="h-9 min-w-0 flex-1 rounded border border-gray-300 px-2 text-sm"
                            disabled={isBusy}
                          />
                        ) : (
                          <h3 className="truncate text-base font-semibold text-gray-900">{company.name}</h3>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[status] || statusStyles.disabled}`}>
                          {statusLabels[status] || status}
                        </span>
                        {isPortalDefault && (
                          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
                            Portal de clientes v1
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">ID {company.id}</p>
                      {status === 'provisioning' && (
                        <div className="mt-3 max-w-xl">
                          <div className="mb-1 flex justify-between text-xs text-gray-500">
                            <span>{provisioning.currentStep || provisioning.step || provisioning.message || 'Preparando entorno'}</span>
                            <span>{Number.isFinite(progress) ? Math.max(0, Math.min(100, progress)) : 0}%</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                            <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${Math.max(0, Math.min(100, progress || 0))}%` }} />
                          </div>
                        </div>
                      )}
                      {status === 'failed' && (provisioning.error || company.provisioningError) && (
                        <p className="mt-2 text-sm text-red-600">{provisioning.error || company.provisioningError}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {isEditing ? (
                        <>
                          <button type="button" onClick={() => handleSaveName(company)} disabled={isBusy || !editingName.trim()} className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Guardar</button>
                          <button type="button" onClick={() => setEditingId('')} disabled={isBusy} className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600">Cancelar</button>
                        </>
                      ) : status !== 'deleted' && status !== 'provisioning' ? (
                        <button type="button" onClick={() => { setEditingId(String(company.id)); setEditingName(company.name || ''); }} disabled={isBusy} className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Editar</button>
                      ) : null}
                      {status === 'active' && (
                        <button type="button" onClick={() => handleDisable(company)} disabled={isBusy} className="rounded border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50">Deshabilitar</button>
                      )}
                      {status === 'disabled' && (
                        <button type="button" onClick={() => runAction(company, enableCompany, { text: 'Empresa habilitada.', context: 'active' })} disabled={isBusy} className="rounded border border-green-300 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-50">Habilitar</button>
                      )}
                      {status === 'failed' && (
                        <button type="button" onClick={() => runAction(company, retryCompanyProvisioning, { text: 'Reintento de aprovisionamiento iniciado.', context: 'provisioning' })} disabled={isBusy} className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Reintentar</button>
                      )}
                      {status === 'deleted' ? (
                        <button type="button" onClick={() => runAction(company, restoreCompany, { text: 'Empresa restaurada en estado deshabilitado.', context: 'disabled' })} disabled={isBusy} className="rounded border border-blue-300 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">Restaurar</button>
                      ) : status !== 'provisioning' && (
                        <button
                          type="button"
                          onClick={() => handleDelete(company)}
                          disabled={isBusy || isPortalDefault}
                          title={isPortalDefault ? 'La empresa del portal de clientes no se puede eliminar.' : undefined}
                          className="rounded border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompaniesSettingsPanel;
